import React, { forwardRef, ForwardRefRenderFunction, useContext } from "react";
import { Link } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormattedMessage } from "react-intl-phraseapp";
import { Beneficiary, OecStatus } from "../@types";
import {
  formatDate,
  getLowIncomeSubsidyStatusLabel,
  getPlanName,
  makePlanDetailsUrl,
  makePlanDisplayId,
  useTranslate,
} from "../helpers";
import { AppStore } from "../app/store";
import { PlanDetailsLink } from ".";

const _BeneficiaryInfo: ForwardRefRenderFunction<
  HTMLUListElement,
  {
    bene: Beneficiary;
    describedBy?: string;
    oecStatus?: OecStatus;
  }
> = ({ bene, describedBy, oecStatus }, ref) => {
  const t = useTranslate();
  const { mctCurrentPlanYear } = useFlags();
  const {
    state: { year, language, nextYearCoverage },
  } = useContext(AppStore.AppContext);

  const forNextYear = year !== mctCurrentPlanYear?.toString();
  const extraHelpLabelKey = forNextYear
    ? "bene_landing.next_year_extra_help"
    : "bene_landing.current_extra_help";

  let currentCoverageElem: React.ReactFragment;
  let testId = "";

  if (bene.current_coverage.length) {
    testId = "current-coverage-name";
    const currentCoverage = bene.current_coverage[0];
    currentCoverageElem = (
      <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
        <dt>{t("app.terms.current_coverage")}</dt>
        <dd>
          {getPlanName(currentCoverage, language)}{" "}
          {makePlanDisplayId(currentCoverage)}{" "}
          <Link
            to={makePlanDetailsUrl({
              plan: currentCoverage,
              language,
              yearValue: "coverage_year",
            })}
          >
            {t("bene_landing.view_plan_details")}
          </Link>
        </dd>
      </dl>
    );
  } else {
    testId = "current-coverage-none";
    currentCoverageElem = (
      <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
        <dt>{t("app.terms.current_coverage")}</dt>
        <dd>{t("none")}</dd>
      </dl>
    );
  }

  let potentialNextCoverageElem: React.ReactFragment = "";

  if (nextYearCoverage && forNextYear) {
    testId = "potential-next-coverage";
    potentialNextCoverageElem = (
      <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
        <dt>
          <FormattedMessage
            id="bene_landing.coverage_with_no_change"
            values={{ year: mctCurrentPlanYear + 1 }}
          />
        </dt>
        <dd>
          {getPlanName(nextYearCoverage, language)}{" "}
          {makePlanDisplayId(nextYearCoverage)}{" "}
          <Link to={makePlanDetailsUrl({ plan: nextYearCoverage, language })}>
            {t("bene_landing.view_plan_details")}
          </Link>
        </dd>
      </dl>
    );
  }

  let futureCoverageNameElem: React.ReactFragment = "";
  let futureCoverageStartOrConfirmationElem: React.ReactFragment = "";

  if (bene.future_coverage.length) {
    const futureCoverage = bene.future_coverage[0];
    futureCoverageNameElem = (
      <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
        <dt>{t("bene_landing.new_coverage")}</dt>
        <dd>
          {getPlanName(futureCoverage, language) || ""}{" "}
          {makePlanDisplayId(futureCoverage)}{" "}
          <Link to={makePlanDetailsUrl({ plan: futureCoverage, language })}>
            {t("bene_landing.view_plan_details")}
          </Link>
        </dd>
      </dl>
    );
    futureCoverageStartOrConfirmationElem = (
      <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
        <dt>{t("bene_landing.coverage_starts")}</dt>
        <dd>
          {formatDate(
            bene.future_coverage[0].enrollment_effective_date,
            "M/d/yyyy"
          )}
        </dd>
      </dl>
    );
  } else if (oecStatus) {
    const plan = {
      ...oecStatus,
      contract_year: oecStatus.year,
    };
    futureCoverageNameElem = (
      <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
        <dt>{t("bene_landing.requested_to_join")}</dt>
        <dd>
          {getPlanName(oecStatus, language) || ""}{" "}
          {makePlanDisplayId(oecStatus)}{" "}
          <Link to={makePlanDetailsUrl({ plan, language })}>
            {t("bene_landing.view_plan_details")}
          </Link>
        </dd>
      </dl>
    );
    futureCoverageStartOrConfirmationElem = (
      <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
        <dt>{t("future_enrollment.confirmation_number")}</dt>
        <dd>
          {oecStatus.confirmation_number}{" "}
          <PlanDetailsLink
            label={t("bene_landing.contact_plan_for_processing_details")}
            plan={{
              ...oecStatus,
              contract_year: oecStatus.year,
            }}
          />
        </dd>
      </dl>
    );
  }

  return (
    <ul
      className="ds-c-list--bare ds-u-margin-y--1"
      role="list"
      ref={ref}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      {...(describedBy ? { "aria-describedby": describedBy } : {})}
    >
      <li className="ds-u-margin-y--1" data-testid={testId} role="listitem">
        {potentialNextCoverageElem || currentCoverageElem}
      </li>
      <li className="ds-u-margin-y--1" role="listitem">
        <dl className="mct-c-definition-list--inline ds-u-margin-y--0">
          <dt data-testid={extraHelpLabelKey}>
            <FormattedMessage id={extraHelpLabelKey} values={{ year }} />
          </dt>
          {year && (
            <dd>
              {t(
                getLowIncomeSubsidyStatusLabel(
                  year,
                  forNextYear ? bene.future_lis_level : bene.lis_level
                )
              )}
            </dd>
          )}
        </dl>
      </li>
      {futureCoverageNameElem && (
        <>
          <li aria-hidden="true" className="ds-u-margin-y--2" role="listitem">
            <hr className="ds-u-border--1" />
          </li>
          <li
            className="ds-u-margin-y--1"
            data-testid="future-enrollment"
            role="listitem"
          >
            {futureCoverageNameElem}
          </li>
          <li
            className="ds-u-margin-y--1"
            data-testid="confirmation"
            role="listitem"
          >
            {futureCoverageStartOrConfirmationElem}
          </li>
        </>
      )}
    </ul>
  );
};

const BeneficiaryInfo = forwardRef(_BeneficiaryInfo);

export default BeneficiaryInfo;
